/**
 * First screen of a new test: pick the page to improve. Presentational:
 * CreateExperiment passes real page traffic, the DEV states gallery passes
 * Paper's mocks.
 *
 * Paper → Experiments: "01 New test · Pick a page · 1440" and "01b New test ·
 * From planner · 1440". The subtitle says "we set up the test" (Paper says
 * "open it in the editor"; picking goes to Setup first). Busiest pages with visitors a day and time to a
 * result; a card under the list opens the split-by-URL (redirect) test.
 */
import { useMemo, useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { Alert02Icon, ArrowRight01Icon, Search01Icon } from "@hugeicons/core-free-icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import { planForPicker, type PickerPage } from "./newTestModel"
import { estimateTestDuration } from "@/lib/utils/abTestStatistics"

export type NewTestPagePickerProps = {
  pages: PickerPage[]
  loading?: boolean
  /** "acme.com", prefixed to each page path on the second line. */
  domain?: string
  /** Planner banner line; the banner renders only when set. */
  planSummary?: string | null
  onPick: (path: string) => void
  onSplitByUrl: () => void
  /** Plan behind the time estimates; defaults to the picker's. */
  plan?: ReturnType<typeof planForPicker>
}

const looksLikeUrl = (q: string) => q.startsWith("/") || /^https?:\/\//i.test(q) || /\.[a-z]{2,}(\/|$)/i.test(q)

/** "/lp/switch" from "/lp/switch", "acme.com/lp/switch" or a full URL. */
function pathOf(q: string): string | null {
  try {
    const url = q.startsWith("/") ? new URL(q, "https://site.invalid") : new URL(/^https?:\/\//i.test(q) ? q : `https://${q}`)
    return url.pathname || "/"
  } catch {
    return null
  }
}

/** "about 3 months" at this many visitors a day. */
function spanAt(daily: number, plan: ReturnType<typeof planForPicker>): string {
  const { days } = estimateTestDuration(daily, plan.baseline / 100, plan.mde, plan.significance)
  if (days <= 60) return `about ${Math.max(1, Math.round(days / 7))} weeks`
  if (days <= 365) return `about ${Math.round(days / 30)} months`
  return "more than a year"
}

export function NewTestPagePicker({
  pages,
  loading = false,
  domain,
  planSummary,
  onPick,
  onSplitByUrl,
  plan = planForPicker(null),
}: NewTestPagePickerProps) {
  const [query, setQuery] = useState("")
  const [active, setActive] = useState(0)

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return pages
    return pages.filter((p) => p.path.toLowerCase().includes(q) || p.title.toLowerCase().includes(q))
  }, [pages, query])

  const pickable = visible.filter((p) => !p.time.disabled)
  const trimmed = query.trim()
  const pastedPath = trimmed && looksLikeUrl(trimmed) ? pathOf(trimmed) : null
  const newPageEstimate = `At 200 visitors a day from ads, ${spanAt(200, plan)}. At 50 a day, ${spanAt(50, plan)}.`
  const activePath = pickable[Math.min(active, pickable.length - 1)]?.path

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActive((i) => Math.min(i + 1, Math.max(pickable.length - 1, 0)))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActive((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const q = query.trim()
      if (activePath) onPick(activePath)
      else if (q && looksLikeUrl(q)) onPick(q)
    }
  }

  return (
    <div className="mx-auto mt-8 flex w-full max-w-[720px] flex-col sm:mt-12">
      <div className="flex flex-col gap-[18px] px-1 pb-6 sm:px-5">
        {planSummary && (
          <div className="flex items-center gap-3.5 rounded-xl bg-canvas px-3.5 py-3">
            <span className="flex size-[30px] shrink-0 items-center justify-center rounded-lg bg-card ring-1 ring-foreground/[0.08]">
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden className="text-foreground/75">
                <path d="M4 5h16M4 12h10M4 19h6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-xs font-medium leading-4 text-muted-foreground">Settings from your plan</span>
              <span className="text-sm font-medium leading-[18px] text-foreground">{planSummary}</span>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <h1 className="text-[28px] font-semibold leading-[34px] tracking-[-0.025em] text-foreground">
            Which page do you want to improve?
          </h1>
          <p className="text-sm leading-5 text-muted-foreground">
            Pick one and we open it in the editor. Everything else is measured for you.
          </p>
        </div>
      </div>

      <label className="mx-1 flex h-10 shrink-0 items-center gap-2.5 rounded-lg px-3 ring-[1.5px] ring-foreground sm:mx-6">
        <HugeiconsIcon icon={Search01Icon} size={15} className="shrink-0 text-foreground/40" />
        <span className="sr-only">Search pages or paste a URL</span>
        <input
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
          }}
          onKeyDown={onKeyDown}
          placeholder="Search pages or paste a URL"
          className="min-w-0 flex-1 bg-transparent text-sm leading-[18px] text-foreground outline-none placeholder:text-foreground/40"
        />
      </label>

      <div className="flex items-center gap-3 px-4 pb-2 pt-[18px] text-xs leading-4 text-muted-foreground sm:px-9">
        <span className="flex-1">Your busiest pages</span>
        <span className="w-[90px] shrink-0 whitespace-nowrap text-right">Visitors / day</span>
        <span className="hidden w-[140px] shrink-0 text-right sm:block">Time to a result</span>
        <span aria-hidden className="w-[14px] shrink-0" />
      </div>

      <div role="listbox" aria-label="Your busiest pages" className="flex flex-col px-1 sm:px-6">
        {loading
          ? [0, 1, 2, 3].map((i) => (
              <div key={i} className="flex h-14 shrink-0 items-center gap-3 px-3">
                <Skeleton className="size-8 shrink-0 rounded-lg" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3.5 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-3.5 w-8" />
              </div>
            ))
          : visible.map((page) => {
              const isActive = page.path === activePath
              return (
                <button
                  key={page.path}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  aria-disabled={page.time.disabled || undefined}
                  disabled={page.time.disabled}
                  onClick={() => onPick(page.path)}
                  onMouseEnter={() => {
                    const i = pickable.findIndex((p) => p.path === page.path)
                    if (i >= 0) setActive(i)
                  }}
                  className={cn(
                    "flex h-14 shrink-0 items-center gap-3 rounded-lg px-3 text-left outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring",
                    isActive && "bg-muted/50 ring-1 ring-foreground/[0.05]",
                    page.time.disabled && "cursor-not-allowed opacity-[0.42]",
                  )}
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-semibold leading-4 text-muted-foreground">
                    {page.glyph}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate text-sm font-medium leading-[18px] text-foreground">{page.title}</span>
                    <span className="truncate text-xs leading-4 text-muted-foreground">
                      {domain ? `${domain}${page.path.startsWith("/") ? "" : "/"}${page.path}` : page.path}
                    </span>
                  </span>
                  <span className="w-[56px] shrink-0 text-right text-[13px] font-medium leading-4 tabular-nums text-foreground sm:w-[90px]">
                    {page.daily.toLocaleString("en-US")}
                  </span>
                  <span
                    className={cn(
                      "hidden w-[140px] shrink-0 text-right text-xs leading-4 sm:block",
                      page.time.slow ? "font-medium text-warning" : "text-muted-foreground",
                    )}
                  >
                    {page.time.label}
                  </span>
                  <HugeiconsIcon
                    icon={ArrowRight01Icon}
                    size={14}
                    strokeWidth={2.14}
                    className={cn(
                      "w-[14px] shrink-0",
                      page.time.disabled ? "opacity-0" : isActive ? "text-foreground/50" : "text-foreground/30",
                    )}
                  />
                </button>
              )
            })}
        {!loading && visible.length === 0 && pastedPath && (
          <>
            {/* A pasted page outside the busiest list: often new, about to get ad traffic. Never blocked. */}
            <button
              type="button"
              role="option"
              aria-selected
              onClick={() => onPick(query.trim())}
              className="flex h-14 shrink-0 items-center gap-3 rounded-lg bg-muted/50 px-3 text-left outline-none ring-1 ring-foreground/[0.05] focus-visible:ring-ring"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-semibold leading-4 text-muted-foreground">
                ◐
              </span>
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-sm font-medium leading-[18px] text-foreground">{pastedPath}</span>
                <span className="truncate text-xs leading-4 text-muted-foreground">
                  Not in your busiest pages · no visits in the last 30 days
                </span>
              </span>
              <span className="w-[56px] shrink-0 text-right text-[13px] font-medium leading-4 tabular-nums text-foreground sm:w-[90px]">
                0
              </span>
              <span className="hidden w-[140px] shrink-0 text-right text-xs font-medium leading-4 text-warning sm:block">
                No estimate yet
              </span>
              <HugeiconsIcon icon={ArrowRight01Icon} size={14} strokeWidth={2.14} className="w-[14px] shrink-0 text-foreground/50" />
            </button>
            <div role="note" className="mt-2 flex gap-3 rounded-lg bg-warning-tint px-4 py-3.5">
              <HugeiconsIcon icon={Alert02Icon} size={16} className="mt-px shrink-0 text-warning" />
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-[13px] font-semibold leading-[18px] text-foreground">
                  New page, no traffic yet. You can still test it.
                </span>
                <span className="text-[13px] leading-[19px] text-foreground/80">
                  A result comes as fast as you send visitors. {newPageEstimate} We’ll show the real estimate once traffic arrives.
                </span>
              </div>
            </div>
          </>
        )}
        {!loading && visible.length === 0 && !pastedPath && (
          <p className="px-3 py-5 text-[13px] leading-5 text-muted-foreground">
            {pages.length === 0 ? "No page traffic yet. Paste a URL to start." : "No pages match. Paste a URL to test it."}
          </p>
        )}
      </div>

      <div className="mx-1 flex items-center gap-3.5 border-t border-foreground/[0.05] px-3 pb-4 pt-3 sm:mx-6">
        <span className="text-xs leading-4 text-muted-foreground">↑↓ to move · ↵ to open in editor</span>
      </div>

      <div className="mx-1 mt-4 flex items-center gap-3.5 rounded-xl bg-card px-4 py-3.5 ring-1 ring-border sm:mx-5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-canvas">
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden className="text-muted-foreground">
            <path
              d="M2 8h4.5M6.5 8l3-4h4.5M6.5 8l3 4h4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium leading-[18px] text-foreground">Testing a new page?</span>
          <span className="text-[13px] leading-4 text-muted-foreground">
            Split visitors between one of these pages and a new URL.
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={onSplitByUrl} className="h-8 shrink-0 px-3.5 text-[13px]">
          Split by URL
        </Button>
      </div>
    </div>
  )
}
